import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { ChatService, ChatMessage } from '../../services/chat.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-chat',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, InputTextModule, TooltipModule],
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss']
})
export class ChatComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('chatHistoryScroll') chatHistoryScroll?: ElementRef;
  
  messages: ChatMessage[] = [];
  latestMessage: ChatMessage | null = null;
  unreadCount: number = 0;
  newMessage: string = '';
  isExpanded: boolean = false;
  
  private messagesSubscription?: Subscription;
  private unreadSubscription?: Subscription;
  private shouldScrollToBottom: boolean = false;

  constructor(public chatService: ChatService) {}

  ngOnInit(): void {
    // Subscribe to chat messages
    this.messagesSubscription = this.chatService.messages$.subscribe(messages => {
      console.log('💬 Chat component received messages:', messages.length);
      messages.forEach((msg, idx) => {
        console.log(`  Message ${idx}: ${msg.senderName} (isSelf=${msg.isSelf}): ${msg.message}`);
      });
      this.messages = messages;
      this.latestMessage = messages.length > 0 ? messages[messages.length - 1] : null;
      this.shouldScrollToBottom = true;
      
      // Auto-expand when first message arrives
      if (messages.length === 1 && !this.isExpanded) {
        this.isExpanded = false; // Start collapsed
      }
    });

    // Subscribe to unread count
    this.unreadSubscription = this.chatService.unreadCount$.subscribe(count => {
      console.log('🔔 Chat component unread count updated:', count);
      this.unreadCount = count;
    });
  }

  ngAfterViewChecked(): void {
    // Auto-scroll to bottom when new messages arrive
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  ngOnDestroy(): void {
    this.messagesSubscription?.unsubscribe();
    this.unreadSubscription?.unsubscribe();
  }

  /**
   * Toggle the preview expansion
   */
  togglePreviewExpansion(): void {
    console.log('Toggling chat expansion from:', this.isExpanded, 'to:', !this.isExpanded);
    this.isExpanded = !this.isExpanded;
    this.chatService.markAsRead();
    this.shouldScrollToBottom = true;
    
    // Force change detection
    setTimeout(() => {
      this.shouldScrollToBottom = true;
    }, 100);
  }

  /**
   * Send a chat message
   */
  async sendMessage(): Promise<void> {
    if (!this.newMessage.trim()) {
      return;
    }

    try {
      await this.chatService.sendMessage(this.newMessage);
      this.newMessage = '';
      this.shouldScrollToBottom = true;
    } catch (error) {
      console.error('Error sending message:', error);
    }
  }

  /**
   * Handle Enter key press in input
   */
  onKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  /**
   * Scroll chat history to bottom
   */
  private scrollToBottom(): void {
    if (this.chatHistoryScroll) {
      try {
        this.chatHistoryScroll.nativeElement.scrollTop = 
          this.chatHistoryScroll.nativeElement.scrollHeight;
      } catch (error) {
        console.error('Error scrolling to bottom:', error);
      }
    }
  }

  /**
   * Get formatted timestamp for a message
   */
  getFormattedTime(message: ChatMessage): string {
    const date = message.timestamp;
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  }

}

